import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { Field, Form, Formik } from 'formik';
import { useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import * as Yup from 'yup';

import Button from '../../../../shared/_TBComponents/Button';
import Checkmark from '../../../../shared/_TBComponents/Checkmark';
import Link from '../../../../shared/_TBComponents/Link';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import useReset from '../hooks/useReset';

const FieldError = styled('div')`
  color: #FF6600;
  font-size: 14px;
  margin-top: 10px;
`;

const FormGroup = styled('div')`  
  .return-link {
    margin-top: 30px;
  }
`;

export const MessageContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
`;

const ResponseMessage = styled('div')`
  color: #FF6600;
  margin-bottom: 50px;
  padding: 0px 20px;
`;

const EmailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid Email Address')
    .required('Email Required'),
});

const ForgotPasswordForm = () => {
  const {
    handleForgot,
  } = useReset();

  const forgotError = useSelector((state) => state.forgot.ui.forgotError);
  const isComplete = useSelector((state) => state.forgot.ui.isComplete);
  const isDisabled = useSelector((state) => state.forgot.ui.isDisabled);
  const isSubmitting = useSelector((state) => state.forgot.ui.isSubmitting);

  const formAnimation = useSpring({
    opacity: isSubmitting || isComplete ? 0 : 1,
    zIndex: 1,
  });

  const spinnerAnimation = useSpring({ opacity: isSubmitting ? 1 : 0, zIndex: 0 });
  const completeAnimation = useSpring({ opacity: isComplete ? 1 : 0 });

  const theme = useTheme();

  return (
    <Formik
      initialValues={{
        email: '',
      }}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          handleForgot(values);
          setSubmitting(false);
        });
      }}
      validationSchema={EmailSchema}
      render={({
        errors,
        touched,
        handleSubmit,
      }) => (
        <>
          <animated.div style={formAnimation}>
            <Form
              id='forgot'
              name='forgot'
            >
              <FormGroup className='form-group'>
                <Text
                  styledTextProps={{ fontSize: theme.textFontSizes[0], fontWeight: '700' }}
                  styledWrapperProps={{ marginBottom: '8px', textAlign: 'center' }}
                >
                  Enter Your Email Address
                </Text>
                <Field
                  className='form-control'
                  disabled={isDisabled}
                  id='email-field'
                  name='email'
                  placeholder='Email'
                  type='email'
                />
                {errors.email && touched.email && <FieldError className='text-center'>{errors.email}</FieldError>}
                {forgotError && <FieldError className='text-center'>Please enter a valid email address and try again</FieldError>}
              </FormGroup>
              <FormGroup className='text-center'>
                <Button
                  bgColor={theme.colors.orange}
                  disabled={isDisabled || errors.email}
                  fontSize='14px'
                  fullWidth
                  id='submit'
                  isLoading={isSubmitting}
                  large
                  lightSpinner
                  name='submit'
                  noBorder
                  onClick={handleSubmit}
                  rounded
                  txtColor={theme.colors.white}
                  type='submit'
                  upperCase
                />
                <Link allCaps className='return-link' color='lightText.1' disabled={isDisabled} href='/login'>
                  Return to Login
                </Link>
              </FormGroup>
            </Form>
          </animated.div>
          <animated.div style={spinnerAnimation}>
            <MessageContainer>
              <Spinner />
            </MessageContainer>
          </animated.div>
          <animated.div style={completeAnimation}>
            <MessageContainer>
              <Checkmark />
              <ResponseMessage className='text-center'>
                If your email is associated with a TeamBuildr account,
                a link to reset your password will be sent to your inbox
              </ResponseMessage>
            </MessageContainer>
          </animated.div>
        </>
      )}
    />
  );
};

export default ForgotPasswordForm;
